import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Sparkles, Upload, ArrowLeft, Loader2, X, Image as ImageIcon, FileText,
  CheckCircle2, ExternalLink, MapPin, Building2,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import HidroMarkdown from "../components/HidroMarkdown";
import VoiceTextarea from "../components/VoiceTextarea";
import { useBranding } from "../context/TenantBrandingContext";

/**
 * Hidro Study — the dedicated full-page surface for the vision intake
 * pipeline. Same backend (`POST /api/hidro/intake`) as the chat-panel
 * paperclip; this screen is for when the user wants more room — e.g.
 * studying a lodge map or a long invoice.
 *
 * Deep-link inputs:
 *   ?customer=<id>  — bind the intake to that customer
 *   ?lead=<id>      — bind the intake to that lead
 *   ?prompt=...     — pre-fill the prompt field
 */
export default function HidroStudy() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [params] = useSearchParams();
  const customerId = params.get("customer") || "";
  const leadId = params.get("lead") || "";
  const initialPrompt = params.get("prompt") || "";

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  // Drag and drop wiring
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDragOver = (e) => { e.preventDefault(); el.classList.add("ring-2", "ring-[#1FB6C1]/60"); };
    const onDragLeave = () => el.classList.remove("ring-2", "ring-[#1FB6C1]/60");
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove("ring-2", "ring-[#1FB6C1]/60");
      const f = e.dataTransfer?.files?.[0];
      if (f) acceptFile(f);
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, []);

  const acceptFile = (f) => {
    if (!/^image\/(jpeg|png|webp)$/i.test(f.type)) {
      setErr("Only JPEG, PNG or WEBP images are supported.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      setErr("Image too large. Max 12 MB.");
      return;
    }
    setErr("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
  };

  const submit = async () => {
    if (!file || busy) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prompt", prompt || "Study this image and tell me what to do next.");
      fd.append("auto_research", "true");
      if (customerId) { fd.append("context_type", "customer"); fd.append("context_id", customerId); }
      else if (leadId) { fd.append("context_type", "lead"); fd.append("context_id", leadId); }
      const { data } = await api.post("/hidro/intake", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      setResult(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, `${aiName} couldn't study that image.`));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-4 pb-12 max-w-4xl mx-auto" data-testid="hidro-study-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} className="hv-btn hv-btn-ghost text-xs" data-testid="hidro-study-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#1FB6C1]" />
            Study with Hidro
          </h1>
          <p className="text-xs text-[#dde3ec] mt-1">
            Drop an image, tell Hidro what you want, and it will pull out the
            entities, recommend prospects, and queue deep research.
          </p>
        </div>
      </div>

      {/* Upload zone */}
      {!result && (
        <div
          ref={dropRef}
          className="hv-card p-6 border-dashed transition cursor-pointer"
          onClick={() => fileRef.current?.click()}
          data-testid="hidro-study-drop"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) acceptFile(f); }}
            className="hidden"
            data-testid="hidro-study-file"
          />
          {previewUrl ? (
            <div className="flex items-start gap-4 flex-wrap">
              <img src={previewUrl} alt="attached" className="rounded-md max-h-60 max-w-xs border border-white/10 object-contain" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{file?.name}</div>
                <div className="text-xs text-[#a0aabf]">{Math.round((file?.size || 0) / 1024)} KB · {file?.type}</div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); clear(); }}
                  className="hv-btn hv-btn-ghost text-xs mt-2"
                  data-testid="hidro-study-clear"
                >
                  <X className="w-3.5 h-3.5" /> Pick a different image
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <Upload className="w-10 h-10 mx-auto text-[#1FB6C1] mb-2" />
              <div className="font-medium">Drop an image here, or click to browse</div>
              <div className="text-xs text-[#a0aabf] mt-1">JPEG, PNG, WEBP — up to 12 MB</div>
            </div>
          )}
        </div>
      )}

      {/* Prompt + submit */}
      {!result && file && (
        <div className="hv-card p-4 space-y-3">
          <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">
            What should Hidro do with it?
          </label>
          <VoiceTextarea
            value={prompt}
            onChange={setPrompt}
            rows={3}
            placeholder="e.g. Study this Sabi Sands lodge map and recommend the top 5 prestige lodges to deep-research."
            data-testid="hidro-study-prompt"
           testIdPrefix="hidro-study-prompt-voice" context="note" />
          {err && <div className="text-xs text-rose-300">{err}</div>}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="hv-btn hv-btn-primary text-sm"
              data-testid="hidro-study-submit"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Studying…</> : <><Sparkles className="w-4 h-4" /> Study with Hidro</>}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-3" data-testid="hidro-study-result">
          {previewUrl && (
            <div className="hv-card p-3 flex items-center gap-3 flex-wrap">
              <img src={previewUrl} alt="studied" className="rounded-md max-h-32 border border-white/10 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold">Studied</div>
                <div className="text-sm font-medium truncate">{result.summary || "(no summary)"}</div>
                <div className="text-xs text-[#a0aabf]">
                  Image kind: <span className="text-white font-mono">{result.image_kind}</span> ·
                  {" "}{result.entities?.length || 0} entities · {result.created_suspect_ids?.length || 0} suspects added · {result.queued_research?.length || 0} research jobs queued
                </div>
              </div>
              <button onClick={clear} className="hv-btn hv-btn-ghost text-xs">
                <Sparkles className="w-3.5 h-3.5" /> Study another
              </button>
            </div>
          )}

          <div className="hv-card p-4 space-y-3">
            <h3 className="font-display text-base font-bold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#1FB6C1]" /> Hidro's analysis
            </h3>
            <HidroMarkdown
              text={result.reply_markdown}
              className="text-sm"
              testId="hidro-study-analysis"
            />
          </div>

          {/* Entities */}
          {result.entities?.length > 0 && (
            <div className="hv-card p-4">
              <h3 className="font-display text-base font-bold mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#A78BFA]" /> Extracted entities ({result.entities.length})
              </h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {result.entities.map((e, i) => (
                  <div
                    key={i}
                    className={`border rounded-md p-3 ${
                      e.recommendation === "deep_research"
                        ? "border-rose-400/40 bg-rose-500/5"
                        : e.recommendation === "watch"
                          ? "border-amber-400/30 bg-amber-500/5"
                          : "border-white/10 bg-white/[0.02]"
                    }`}
                    data-testid={`hidro-study-entity-${i}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-white truncate">{e.name}</div>
                        <div className="text-[10px] text-[#9aa3b5]">
                          {e.tier && <span className="uppercase tracking-wider mr-1">{e.tier}</span>}
                          {e.estimated_star_rating && <span className="text-amber-300 mr-1">{"★".repeat(e.estimated_star_rating)}</span>}
                          {e.estimated_price_band && <span className="text-emerald-300">{e.estimated_price_band}</span>}
                        </div>
                      </div>
                      <span
                        className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${
                          e.recommendation === "deep_research"
                            ? "bg-rose-500/20 text-rose-300 border border-rose-400/40"
                            : e.recommendation === "watch"
                              ? "bg-amber-500/20 text-amber-300 border border-amber-400/40"
                              : "bg-white/5 text-[#9aa3b5] border border-white/10"
                        }`}
                      >
                        {e.recommendation || "—"}
                      </span>
                    </div>
                    {e.rationale && (
                      <div className="text-[11px] text-[#dde3ec] mt-1.5">{e.rationale}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick links */}
          <div className="hv-card p-4 space-y-2">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Where Hidro saved this
            </h3>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <Link to="/suspects" className="hv-btn hv-btn-ghost text-xs justify-start">
                <Building2 className="w-3.5 h-3.5" /> Open Suspects ({result.created_suspect_ids?.length || 0} new)
              </Link>
              <Link to="/research" className="hv-btn hv-btn-ghost text-xs justify-start">
                <Sparkles className="w-3.5 h-3.5" /> Open Deep Research index
              </Link>
            </div>
            {(result.queued_research?.length || 0) > 0 && (
              <div className="text-[11px] text-[#9aa3b5] italic mt-1">
                You'll get a notification when each deep-research job is ready —
                tap it to land directly on the brief.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
